import { Button, Grid } from '@mui/material';
import { FormAutoCompleteMultiSelectDynamic } from '../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { marketAreaApiKeyStore } from '../../api/masters/market-area-api-key-store';
import { skillSetApiKeyStore } from '../../api/masters/skill-set-api-key-store';
import { equipmentApiKeyStore } from '../../api/masters/equipment-api-key-store';
import { divisionApiKeyStore } from '../../api/masters/division-api-key-store';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { setValue } from '../../components/hook-form/form-input.time.component';
import { useFormContext } from 'react-hook-form';

type InspectorFilterState = {
  userIDs: number[];
  marketAreaIDs: number[];
  skillsetIDs: number[];
  equipmentIDs: number[];
  divisionIDs: number[];
  fromDateForInspector: string;
  toDateForInspector: string;
};

interface InspectorsFiltersProps {
  setInspectorFilter: React.Dispatch<React.SetStateAction<InspectorFilterState>>;
  inspectorFilter: InspectorFilterState;
  handleInspectorFilterClick: () => void;
}

const TIME_PERIOD = [
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
];

const InspectorsFilters = ({
  setInspectorFilter,
  inspectorFilter,
  handleInspectorFilterClick,
}: InspectorsFiltersProps) => {
  const handleFilterChange = (key: keyof typeof inspectorFilter, values: any) => {
    setInspectorFilter((prev) => ({
      ...prev,
      [key]: values,
    }));
  };

  const { setValue, watch } = useFormContext();

  // useEffect(() => {
  //   setValue('timePeriodForInspector', [{ label: 'Today', value: 'today' }]);
  //   handleTimePeriodChange([{ label: 'Today', value: 'today' }]);
  // }, []);

  const handleFilterClick = () => {
    // if (!Array.isArray(watch('timePeriodForInspector')) || watch('timePeriodForInspector')?.length === 0) {
    //   setValue('timePeriodForInspector', [{ label: 'Today', value: 'today' }]);
    // }
    handleInspectorFilterClick();
  };

  const HOLIDAYS: string[] = []; // Add more holidays in MM/DD/YYYY format

  const isBusinessDay = (date: dayjs.Dayjs) => {
    const day = date.day(); // 0 (Sunday) - 6 (Saturday)
    const isWeekend = day === 0 || day === 6;
    const isHoliday = HOLIDAYS.includes(date.format('MM/DD/YYYY'));
    return !isWeekend && !isHoliday;
  };

  const getNextBusinessDay = (startDate: dayjs.Dayjs) => {
    let nextDate = startDate.add(1, 'day');
    while (!isBusinessDay(nextDate)) {
      nextDate = nextDate.add(1, 'day');
    }
    return nextDate;
  };

  const handleTimePeriodChange = (selectedValues: { label: string; value: string }[]) => {
    if (selectedValues?.length === 0) {
      // handleTimePeriodChange([{ label: 'Today', value: 'today' }]);
      handleFilterChange('fromDateForInspector', "");
      handleFilterChange('toDateForInspector', "");
    } else {
      const hasToday = selectedValues.some((item) => item.value === 'today');
      const hasTomorrow = selectedValues.some((item) => item.value === 'tomorrow');

      let fromDate: string | null = null;
      let toDate: string | null = null;

      const today = dayjs();

      if (hasToday && hasTomorrow) {
        const from = isBusinessDay(today) ? today : getNextBusinessDay(today);
        const to = getNextBusinessDay(from);
        fromDate = from.format('MM/DD/YYYY');
        toDate = to.format('MM/DD/YYYY');
      } else if (hasToday) {
        const from = isBusinessDay(today) ? today : getNextBusinessDay(today);
        fromDate = from.format('MM/DD/YYYY');
        toDate = fromDate;
      } else if (hasTomorrow) {
        const to = getNextBusinessDay(today);
        fromDate = to.format('MM/DD/YYYY');
        toDate = fromDate;
      }

      handleFilterChange('fromDateForInspector', fromDate);
      handleFilterChange('toDateForInspector', toDate);
    }
  };
  return (
    <>
      <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="inspectorFilter.userIDs"
          label="Inspectors"
          getQueryKeyConfig={() => usersApiKeyStore.getUsersByRole('ins')}
          optionsLabelKey="fullName"
          optionsValueKey="userID"
          onChange={(values: any) => handleFilterChange('userIDs', values)}
        />
      </Grid>

      <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="inspectorFilter.marketAreaIDs"
          label="Market Areas"
          getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
          optionsLabelKey="marketAreaName"
          optionsValueKey="marketAreaID"
          onChange={(values: any) => handleFilterChange('marketAreaIDs', values)}
        />
      </Grid>

      <Grid item lg={5} sm={5} xl={5} xs={5}>
        <FormAutoCompleteMultiSelectDynamic
          name="inspectorFilter.skillsetIDs"
          label="Skills"
          getQueryKeyConfig={() => skillSetApiKeyStore.getSkillSets()}
          optionsLabelKey="skillsetName"
          optionsValueKey="skillsetID"
          onChange={(values: any) => handleFilterChange('skillsetIDs', values)}
        />
      </Grid>

      <Grid item lg={5} sm={5} xl={5} xs={5}>
        <FormAutoCompleteMultiSelectDynamic
          name="inspectorFilter.equipmentIDs"
          label="Equipments"
          getQueryKeyConfig={() => equipmentApiKeyStore.getEquipments()}
          optionsLabelKey="equipmentName"
          optionsValueKey="equipmentID"
          onChange={(values: any) => handleFilterChange('equipmentIDs', values)}
        />
      </Grid>
      <Grid item lg={3} sm={6} xl={3} xs={6}>
        <FormAutoCompleteMultiSelectDynamic
          name="inspectorFilter.divisionIDs"
          label="Divisions"
          getQueryKeyConfig={() => divisionApiKeyStore.getDivisions()}
          optionsLabelKey="divisionName"
          optionsValueKey="divisionID"
          onChange={(values: any) => handleFilterChange('divisionIDs', values)}
        />
      </Grid>
      <Grid item lg={5} sm={5} xl={5} xs={5}>
        <FormAutoCompleteMultiSelectDynamic
          name="timePeriodForInspector"
          label="Time Period"
          dataItems={TIME_PERIOD}
          optionsLabelKey="label"
          optionsValueKey="value"
          onChange={(values: any) => handleTimePeriodChange(values)}
        />
      </Grid>
      <Grid item lg={2} sm={2} xl={2} xs={2}>
        <Button type="button" onClick={handleFilterClick} title="Filter" className="btn btn-primary btn-Filter">
          Filter
        </Button>
      </Grid>
    </>
  );
};

export default InspectorsFilters;
